import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { OKTA_AUTH } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { FRUser, TokenStorage } from "@forgerock/javascript-sdk";
import { environment } from "../../environments/environment";
import { WebstorageService } from "./webstorage.service";
import { TAB_KEYS, getTabValue, setTabValue, removeTabValue, jwtSubject } from "./tab-session";

export function isForgeRockDirectory(): boolean {
    return ['ForgeRock DS', 'ForgeRock_IM', 'ForgeRock_IC'].includes(environment.DIRECTORY);
}

/**
 * Sign-in state of this tab. The identity SDKs keep their tokens in sessionStorage (see app.module.ts and
 * app.component.ts), so signing out here ends only this tab's session; signOutEverywhere also ends the identity
 * provider's browser session, which every tab relies on for its next sign-in.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {

    constructor(private router: Router, private storage: WebstorageService, private msal: MsalService, @Inject(OKTA_AUTH) private oktaAuth: OktaAuth) {}

    /**
     * Records which account this tab is signed in with. When it differs from the account the tab's organization was
     * selected under (a different user signed in here), that selection is dropped instead of being reused.
     */
    bindAccount(accountId: string | null): void {
        if (!accountId) return;
        const previous = getTabValue(TAB_KEYS.accountId);
        if (previous && previous !== accountId) {
            this.storage.clearPartner();
        }
        setTabValue(TAB_KEYS.accountId, accountId);
    }

    /** True once the tab's next sign-in should let the user pick an account instead of reusing the provider session. */
    takeAccountPrompt(): boolean {
        const prompt = getTabValue(TAB_KEYS.promptAccount) === 'true';
        removeTabValue(TAB_KEYS.promptAccount);
        return prompt;
    }

    async currentAccountId(): Promise<string | null> {
        if (environment.DIRECTORY === 'Azure') {
            return this.msal.instance.getActiveAccount()?.homeAccountId || null;
        }
        if (environment.DIRECTORY === 'Okta') {
            const idToken = this.oktaAuth.getIdToken();
            return idToken ? jwtSubject(idToken) : null;
        }
        if (isForgeRockDirectory()) {
            const tokens = await TokenStorage.get();
            return tokens?.idToken ? jwtSubject(tokens.idToken) : null;
        }
        return null;
    }

    /**
     * Signs this tab out: its tokens and application session are removed, other tabs keep working. The identity
     * provider session stays, so the next sign-in here offers an account choice rather than silently reusing it.
     */
    async signOutThisTab(reason: 'signedOut' | 'idleTimeout' = 'signedOut'): Promise<void> {
        try {
            await this.clearThisTabTokens();
        } finally {
            this.storage.removeAllData();
            setTabValue(TAB_KEYS.promptAccount, 'true');
            this.router.navigate(['/home/signed-out'], { queryParams: { reason } });
        }
    }

    /** Ends the identity provider's session too. Other tabs keep their current tokens but must sign in again after that. */
    async signOutEverywhere(): Promise<void> {
        this.storage.removeAllData();
        if (environment.DIRECTORY === 'Azure') {
            await this.msal.instance.logoutRedirect({ account: this.msal.instance.getActiveAccount() });
        } else if (environment.DIRECTORY === 'Okta') {
            await this.oktaAuth.signOut();
        } else if (isForgeRockDirectory()) {
            await FRUser.logout().finally(() => TokenStorage.remove().then(() => this.router.navigateByUrl('/')));
        }
    }

    private async clearThisTabTokens(): Promise<void> {
        if (environment.DIRECTORY === 'Azure') {
            const account = this.msal.instance.getActiveAccount();
            // Clears this tab's MSAL cache for the account; returning false stops the redirect to the provider's logout page.
            await this.msal.instance.logoutRedirect({ account, onRedirectNavigate: () => false });
        } else if (environment.DIRECTORY === 'Okta') {
            this.oktaAuth.tokenManager.clear();
        } else if (isForgeRockDirectory()) {
            await TokenStorage.remove();
        }
    }
}
